import { motion } from "framer-motion";
import { X, Lock, Sparkles, BookOpen, Sun, Book } from "lucide-react";
import { PeriodCards } from "./PeriodCards";
import { TrackerCard } from "./TrackerCard";
import { getDayDua, getPeriodForDay } from "@/data/dailyDuas";
import { getDayHadith } from "@/data/dailyHadiths";

interface PreviewDashboardProps {
  onClose: () => void;
  onSignUp: () => void;
}

const DEMO_DAY = 5;

const DEMO_TRACKER_DATA = {
  prayer: { progress: 60, subtitle: "3/5 prayers today" },
  quran: { progress: 45, subtitle: "9 pages • Juz 1" },
  fasting: { progress: 100, subtitle: "5 days fasted" },
  deeds: { progress: 67, subtitle: "2 deeds today" },
  dhikr: { progress: 30, subtitle: "120 dhikr today" },
};

const TRACKER_CONFIG = {
  prayer: { title: "Prayer", icon: "🕌", color: "hsl(152, 60%, 45%)" },
  quran: { title: "Quran", icon: "📖", color: "hsl(42, 70%, 50%)" },
  fasting: { title: "Fasting", icon: "🌙", color: "hsl(280, 60%, 55%)" },
  deeds: { title: "Good Deeds", icon: "💚", color: "hsl(340, 65%, 55%)" },
  dhikr: { title: "Dhikr", icon: "📿", color: "hsl(170, 60%, 45%)" },
};

type TrackerType = "prayer" | "quran" | "fasting" | "deeds" | "dhikr";
const trackers: TrackerType[] = ["prayer", "quran", "fasting", "deeds", "dhikr"];

export const PreviewDashboard = ({ onClose, onSignUp }: PreviewDashboardProps) => {
  const dua = getDayDua(DEMO_DAY);
  const period = getPeriodForDay(DEMO_DAY);
  const hadith = getDayHadith(DEMO_DAY);

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky preview banner */}
      <motion.div
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-50 bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shadow-md"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="w-4 h-4" />
          <span>Preview Mode</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onSignUp}
            className="px-4 py-1.5 bg-background text-foreground rounded-md text-xs font-medium hover:bg-accent transition-colors"
          >
            Start Your Journey
          </button>
          <button onClick={onClose} className="p-1 hover:bg-primary-foreground/10 rounded-full transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Day header (static) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-1"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Day</p>
          <h1 className="font-display text-4xl text-foreground">{DEMO_DAY}</h1>
          <p className="text-sm text-muted-foreground">of Ramadan</p>
        </motion.div>

        {/* Period Cards */}
        <PeriodCards currentDay={DEMO_DAY} />

        {/* Today's Focus (read-only) */}
        {dua && period && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border/50 rounded-xl overflow-hidden"
          >
            <div className="bg-gradient-to-br from-primary/10 to-accent/20 px-6 py-4 border-b border-border/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-display text-lg text-foreground">Today's Focus</h2>
                  <p className="text-xs text-muted-foreground">Day {DEMO_DAY}</p>
                </div>
              </div>
            </div>

            {hadith && (
              <div className="px-6 py-5 border-b border-border/30">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Book className="w-3.5 h-3.5" />
                  <span className="uppercase tracking-wider">Hadith of the Day</span>
                </div>
                <p className="font-arabic text-xl text-foreground leading-loose text-center mb-3" dir="rtl">
                  {hadith.arabic}
                </p>
                <p className="text-sm italic text-muted-foreground text-center mb-3">
                  {hadith.transliteration}
                </p>
                <blockquote className="text-center text-foreground/90 leading-relaxed border-l-2 border-accent/50 pl-4 py-2 bg-accent/10 rounded-r-md mb-2">
                  "{hadith.translation}"
                </blockquote>
                <p className="text-xs text-muted-foreground text-center uppercase tracking-wider">
                  — {hadith.source}
                </p>
              </div>
            )}

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BookOpen className="w-3.5 h-3.5" />
                <span className="uppercase tracking-wider">Daily Dua</span>
              </div>
              <p className="font-arabic text-2xl text-foreground leading-loose text-center" dir="rtl">
                {dua.arabic}
              </p>
              <p className="text-sm italic text-muted-foreground text-center">
                {dua.transliteration}
              </p>
              <blockquote className="text-center text-foreground/90 leading-relaxed border-l-2 border-primary/30 pl-4 py-2 bg-muted/30 rounded-r-md">
                "{dua.translation}"
              </blockquote>
              <p className="text-xs text-muted-foreground text-center uppercase tracking-wider">
                — {dua.source}
              </p>

              {/* Affirmation */}
              <div className="pt-4 mt-4 border-t border-border/30">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Sun className="w-3.5 h-3.5" />
                  <span className="uppercase tracking-wider">Affirmation</span>
                </div>
                <p className="text-foreground italic leading-relaxed text-center">
                  "{period.affirmations[(DEMO_DAY - 1) % period.affirmations.length]}"
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tracker Cards (static, non-interactive) */}
        <section className="space-y-3">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground px-1">
            Today's Trackers
          </h2>
          {trackers.map((tracker) => (
            <TrackerCard
              key={tracker}
              title={TRACKER_CONFIG[tracker].title}
              icon={TRACKER_CONFIG[tracker].icon}
              progress={DEMO_TRACKER_DATA[tracker].progress}
              color={TRACKER_CONFIG[tracker].color}
              subtitle={DEMO_TRACKER_DATA[tracker].subtitle}
              onClick={() => {}}
              isExpanded={false}
            />
          ))}
        </section>

        {/* CTA to sign up */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border-2 border-primary/30 rounded-xl p-8 text-center space-y-4"
        >
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h3 className="font-display text-xl text-foreground">
            Ready to Track Your Ramadan?
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Create a free account to save your progress, track prayers, Quran reading, fasting, and more across all 30 days.
          </p>
          <button
            onClick={onSignUp}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Start Your Journey — Free
          </button>
          <p className="text-xs text-muted-foreground">
            Or continue as a guest • No account required
          </p>
        </motion.div>
      </div>
    </div>
  );
};
